import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

const API_BASE = "https://dashboard.jyotirvani.com";

const getUserOrderKey = (key) => {
  const user = JSON.parse(localStorage.getItem("user") || "null");
  return user?.email ? `${key}_${user.email}` : `${key}_guest`;
};

const OrderCreated = () => {
  const { orderId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const paymentStatus = searchParams.get("payment") || "success";
  const [tracking, setTracking] = useState(null);
  const [loading, setLoading] = useState(false);

  const pendingOrder = useMemo(() => {
    const userOrder =
      JSON.parse(localStorage.getItem(getUserOrderKey("pendingOrder"))) || null;

    const normalOrder =
      JSON.parse(localStorage.getItem("pendingOrder")) || null;

    if (userOrder?.order_id === orderId) return userOrder;
    if (normalOrder?.order_id === orderId) return normalOrder;

    return {};
  }, [orderId]);

  const checkoutSummary = useMemo(() => {
    return JSON.parse(localStorage.getItem("checkoutSummary")) || {};
  }, []);

  const customer = pendingOrder.customer || {};
  const cart = pendingOrder.cart || checkoutSummary.cart || [];

  const totalItems = pendingOrder.totalItems || checkoutSummary.totalItems || 0;
  const totalPrice = pendingOrder.totalPrice || checkoutSummary.totalPrice || 0;
  const deliveryCharge =
    pendingOrder.deliveryCharge || checkoutSummary.deliveryCharge || 0;
  const grandTotal = pendingOrder.grandTotal || checkoutSummary.grandTotal || 0;

  const fetchTracking = useCallback(async () => {
    if (!orderId) return;

    try {
      setLoading(true);

      const res = await fetch(`${API_BASE}/api/track-order/${orderId}`, {
        headers: { Accept: "application/json" },
      });

      const data = await res.json();
      setTracking(data?.data || data);
    } catch (error) {
      console.error("Tracking Error:", error);
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    fetchTracking();
    const interval = setInterval(fetchTracking, 30000);

    return () => clearInterval(interval);
  }, [fetchTracking]);

  const awb =
    tracking?.awb_code ||
    tracking?.awb ||
    tracking?.tracking_number ||
    "AWB not assigned yet";

  const orderStatus =
    tracking?.order_status ||
    tracking?.shipment_status ||
    (paymentStatus === "success" ? "Order Placed" : "Payment Pending");

  return (
    <>
      <style>{`
        .oc-page {
          min-height: 100vh;
          background:
            radial-gradient(circle at top left, rgba(255, 237, 213, 0.95), transparent 32%),
            radial-gradient(circle at bottom right, rgba(237, 233, 254, 0.95), transparent 35%),
            linear-gradient(180deg, #fffaf5 0%, #ffffff 55%, #f8f5ff 100%);
          padding: 30px 16px;
          font-family: Arial, sans-serif;
          color: #111827;
        }

        .oc-container {
          max-width: 1180px;
          margin: 0 auto;
        }

        .oc-hero {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 18px;
          background: linear-gradient(135deg, #3b0764, #7c3aed, #f97316);
          border-radius: 28px;
          padding: 32px 26px;
          color: #fff;
          box-shadow: 0 22px 45px rgba(76,29,149,0.25);
          margin-bottom: 24px;
        }

        .oc-hero h1 {
          margin: 0;
          font-size: clamp(24px, 4vw, 36px);
          font-weight: 900;
          line-height: 1.15;
        }

        .oc-hero p {
          margin: 10px 0 0;
          color: rgba(255,255,255,0.9);
          font-size: 15px;
        }

        .oc-refresh {
          border: none;
          border-radius: 999px;
          padding: 13px 20px;
          background: #fff;
          color: #6d28d9;
          font-weight: 900;
          cursor: pointer;
          white-space: nowrap;
        }

        .oc-grid {
          display: grid;
          grid-template-columns: minmax(0, 1fr) 380px;
          gap: 24px;
          align-items: start;
        }

        .oc-left,
        .oc-right {
          display: grid;
          gap: 20px;
        }

        .oc-right {
          position: sticky;
          top: 18px;
        }

        .oc-card,
        .oc-help {
          background: rgba(255,255,255,0.92);
          border: 1px solid rgba(237,233,254,0.9);
          border-radius: 24px;
          padding: 24px;
          box-shadow: 0 18px 45px rgba(15,23,42,0.08);
          backdrop-filter: blur(12px);
        }

        .oc-card h2,
        .oc-help h2 {
          margin: 0 0 18px;
          font-size: 22px;
          font-weight: 900;
        }

        .oc-product {
          display: flex;
          align-items: center;
          gap: 14px;
          background: #f9fafb;
          border-radius: 16px;
          padding: 12px;
          margin-bottom: 10px;
        }

        .oc-product img {
          width: 62px;
          height: 62px;
          border-radius: 14px;
          object-fit: cover;
          flex-shrink: 0;
        }

        .oc-product h4 {
          margin: 0 0 6px;
          font-size: 15px;
        }

        .oc-muted {
          margin: 0;
          color: #6b7280;
          font-size: 14px;
        }

        .oc-divider {
          height: 1px;
          background: #ede9fe;
          margin: 18px 0;
        }

        .oc-info {
          display: flex;
          justify-content: space-between;
          gap: 14px;
          padding: 12px 0;
          border-bottom: 1px solid #eeeeee;
        }

        .oc-info span {
          color: #6b7280;
          font-size: 14px;
        }

        .oc-info strong {
          color: #111827;
          text-align: right;
          word-break: break-word;
          font-size: 14px;
        }

        .oc-address {
          display: flex;
          gap: 16px;
          align-items: flex-start;
        }

        .oc-address-icon {
          width: 58px;
          height: 58px;
          border-radius: 18px;
          background: #f5f3ff;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 27px;
          flex-shrink: 0;
        }

        .oc-address p {
          margin: 0;
          color: #4b5563;
          line-height: 1.8;
          font-size: 15px;
        }

        .oc-total {
          margin-top: 16px;
          padding: 16px;
          border-radius: 18px;
          background: linear-gradient(135deg, #f5f3ff, #fff7ed);
          color: #4c1d95;
          display: flex;
          justify-content: space-between;
          font-size: 20px;
          font-weight: 900;
        }

        .oc-live-card {
          padding: 20px;
        }

        .oc-live-box {
          min-height: 74px;
          background: linear-gradient(135deg, #f7f2ff, #fff);
          border-radius: 22px;
          padding: 14px 16px;
          border: 1px solid #ece3ff;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 14px;
        }

        .oc-live-left {
          display: flex;
          align-items: center;
          gap: 13px;
          min-width: 0;
        }

        .oc-live-icon {
          width: 44px;
          height: 44px;
          border-radius: 16px;
          background: #fff;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 27px;
          box-shadow: 0 8px 18px rgba(124,58,237,0.12);
          flex-shrink: 0;
        }

        .oc-live-left h3 {
          margin: 0;
          font-size: 18px;
          font-weight: 900;
          color: #0f172a;
          white-space: nowrap;
        }

        .oc-track-btn {
          border: none;
          border-radius: 16px;
          padding: 12px 22px;
          background: linear-gradient(135deg,#16a34a,#22c55e);
          color: #fff;
          font-size: 16px;
          font-weight: 900;
          cursor: pointer;
          box-shadow: 0 8px 20px rgba(34,197,94,0.22);
          white-space: nowrap;
        }

        .oc-help {
          margin-top: 24px;
        }

        .oc-help p {
          margin: 0 0 18px;
          color: #6b7280;
          line-height: 1.6;
        }

        .oc-help-actions {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
        }

        .oc-help-actions a {
          text-decoration: none;
          padding: 12px 16px;
          border-radius: 999px;
          font-weight: 900;
          font-size: 14px;
        }

        .oc-mail {
          background: #f5f3ff;
          color: #6d28d9;
        }

        .oc-phone {
          background: #ecfdf5;
          color: #16a34a;
        }

        @media (max-width: 900px) {
          .oc-grid {
            grid-template-columns: 1fr;
          }

          .oc-right {
            position: static;
          }

          .oc-hero {
            flex-direction: column;
            align-items: flex-start;
          }

          .oc-refresh {
            width: 100%;
          }
        }

        @media (max-width: 600px) {
          .oc-page {
            padding: 16px 10px;
          }

          .oc-hero {
            border-radius: 22px;
            padding: 24px 18px;
          }

          .oc-card,
          .oc-help {
            border-radius: 20px;
            padding: 18px;
          }

          .oc-card h2,
          .oc-help h2 {
            font-size: 19px;
          }

          .oc-product {
            align-items: flex-start;
          }

          .oc-product img {
            width: 54px;
            height: 54px;
          }

          .oc-info {
            flex-direction: column;
            gap: 5px;
          }

          .oc-info strong {
            text-align: left;
          }

          .oc-address {
            gap: 12px;
          }

          .oc-address-icon {
            width: 48px;
            height: 48px;
            font-size: 23px;
          }

          .oc-total {
            font-size: 17px;
          }

          .oc-live-card {
            padding: 16px;
          }

          .oc-live-box {
            min-height: 64px;
            padding: 12px;
            border-radius: 18px;
          }

          .oc-live-icon {
            width: 38px;
            height: 38px;
            font-size: 23px;
            border-radius: 14px;
          }

          .oc-live-left h3 {
            font-size: 15px;
          }

          .oc-track-btn {
            padding: 11px 16px;
            font-size: 14px;
            border-radius: 14px;
          }

          .oc-help-actions a {
            width: 100%;
            text-align: center;
          }
        }
      `}</style>

      <div className="oc-page">
        <div className="oc-container">
          <div className="oc-hero">
            <div>
              <h1>Your Order Has Been Created</h1>
              <p>Track your order status, payment, and delivery updates live.</p>
            </div>

            <button onClick={fetchTracking} className="oc-refresh">
              {loading ? "Updating..." : "Refresh Live Status"}
            </button>
          </div>

          <div className="oc-grid">
            <div className="oc-left">
              <Card title="Products">
                {cart.length === 0 ? (
                  <p className="oc-muted">Product details not available.</p>
                ) : (
                  cart.map((item, index) => (
                    <div key={index} className="oc-product">
                      <img src={item.image} alt={item.name} />

                      <div style={{ flex: 1 }}>
                        <h4>{item.name}</h4>

                        {(item.size || item.unit) && (
                          <p
                            style={{
                              margin: "0 0 5px",
                              color: "#7c3aed",
                              fontWeight: 800,
                              fontSize: "13px",
                            }}
                          >
                            {item.size} {item.unit}
                          </p>
                        )}

                        <p className="oc-muted">
                          Qty: {item.quantity || 1} × ₹{item.price}
                        </p>
                      </div>

                      <strong>
                        ₹{Number(item.price || 0) * Number(item.quantity || 1)}
                      </strong>
                    </div>
                  ))
                )}

                <div className="oc-divider" />

                <Info label="Order ID" value={orderId} />
                <Info label="Tracking Number" value={awb} />
                <Info label="Current Status" value={orderStatus} />
              </Card>

              <Card title="Delivery Details">
                <div className="oc-address">
                  <div className="oc-address-icon">📍</div>

                  <p>
                    <strong>{customer.name || "Customer"}</strong>
                    <br />
                    {customer.address || "Address not available"},{" "}
                    {customer.city || "City not available"},{" "}
                    {customer.state || "State not available"},{" "}
                    {customer.pincode || "Pincode not available"}
                    <br />
                    {customer.phone || "Not available"} •{" "}
                    {customer.email || "Not available"}
                  </p>
                </div>
              </Card>
            </div>

            <div className="oc-right">
              <Card title="Payment Details">
                <Info label="Order ID" value={orderId} />
                <Info
                  label="Payment Status"
                  value={paymentStatus === "success" ? "Paid" : "Pending"}
                />
                <Info label="Payment Mode" value="Razorpay Secure Payment" />
                <Info
                  label={`Subtotal (${totalItems} items)`}
                  value={`₹${totalPrice}`}
                />
                <Info
                  label="Delivery Charge"
                  value={deliveryCharge === 0 ? "Free" : `₹${deliveryCharge}`}
                />

                <div className="oc-total">
                  <span>Total Paid</span>
                  <strong>₹{grandTotal}</strong>
                </div>
              </Card>

              <Card title="Live Tracking" className="oc-live-card">
                <div className="oc-live-box">
                  <div className="oc-live-left">
                    <div className="oc-live-icon">📦</div>
                    <h3>Order Placed</h3>
                  </div>

                  <button
                    className="oc-track-btn"
                    onClick={() =>
                      navigate(`/TrackOrder/${orderId}?payment=${paymentStatus}`)
                    }
                  >
                    Track
                  </button>
                </div>
              </Card>
            </div>
          </div>

          <div className="oc-help">
            <h2>Need Help?</h2>
            <p>
              Payment, product, delivery, or tracking related issues? Please
              contact our support team below.
            </p>

            <div className="oc-help-actions">
              <a href="mailto:support@jyotirvani.com" className="oc-mail">
                📧 support@jyotirvani.com
              </a>

              <a href="tel:+919616339090" className="oc-phone">
                📞 +91 96163 39090
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const Card = ({ title, children, className = "" }) => (
  <div className={`oc-card ${className}`}>
    <h2>{title}</h2>
    {children}
  </div>
);

const Info = ({ label, value }) => (
  <div className="oc-info">
    <span>{label}</span>
    <strong>{value}</strong>
  </div>
);

export default OrderCreated;